import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ClipboardCheck, Plus, Loader2, X, Check, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/utils/datetime";

const PRIO_TONE: Record<string, { bg: string; fg: string }> = {
    low: { bg: "var(--neutral-bg)", fg: "var(--neutral-fg)" },
    normal: { bg: "var(--info-bg)", fg: "var(--info-fg)" },
    high: { bg: "var(--warning-bg)", fg: "var(--warning-fg)" },
    urgent: { bg: "var(--error-bg)", fg: "var(--error-fg)" },
};
const STATUS_TONE: Record<string, { bg: string; fg: string }> = {
    new: { bg: "var(--info-bg)", fg: "var(--info-fg)" },
    in_progress: { bg: "var(--warning-bg)", fg: "var(--warning-fg)" },
    done: { bg: "var(--success-bg)", fg: "var(--success-fg)" },
    cancelled: { bg: "var(--neutral-bg)", fg: "var(--neutral-fg)" },
};

export default function Tasks() {
    const { t } = useTranslation();
    const { me } = useAuth();
    const [scope, setScope] = useState<"mine" | "all">("mine");
    const [rows, setRows] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);
    const [creating, setCreating] = useState(false);
    const [confirmId, setConfirmId] = useState<string | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [form, setForm] = useState<{ title: string; priority: string; assignee_user_id: string }>({ title: "", priority: "normal", assignee_user_id: "" });

    const load = async () => {
        setLoading(true); setErr(null);
        try {
            const r = await api.get(`/tasks?scope=${scope}`);
            setRows(r.data.items || []);
        } catch (e: any) { setErr(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [scope]);

    const create = async () => {
        if (!form.title.trim()) return;
        try {
            await api.post("/tasks", {
                title: form.title,
                priority: form.priority,
                assignee_user_id: form.assignee_user_id || me?.user?.id,
            });
            setCreating(false); setForm({ title: "", priority: "normal", assignee_user_id: "" });
            await load();
        } catch (e: any) { setErr(formatApiError(e)); }
    };

    const toggle = async (t: any) => {
        const next = t.status === "done" ? "new" : "done";
        try { await api.patch(`/tasks/${t.id}`, { status: next }); await load(); }
        catch (e: any) { setErr(formatApiError(e)); }
    };

    const remove = async (id: string) => {
        setDeleting(true);
        try { await api.delete(`/tasks/${id}`); setConfirmId(null); toast.success(t("tasks.deleted")); await load(); }
        catch (e: any) { toast.error(formatApiError(e)); }
        finally { setDeleting(false); }
    };

    return (
        <div className="space-y-4" data-testid="tasks-page">
            <header className="flex items-center gap-3">
                <ClipboardCheck size={22} style={{ color: "var(--primary)" }} />
                <h1 className="text-xl font-semibold flex-1" style={{ color: "var(--text)" }}>{t("tasks.title")}</h1>
                <div className="hidden sm:flex gap-1 rounded-md border p-0.5" style={{ borderColor: "var(--border)" }}>
                    {(["mine", "all"] as const).map((s) => (
                        <button key={s} data-testid={`tasks-scope-${s}`} onClick={() => setScope(s)}
                            className="px-2.5 py-1 text-xs font-medium rounded"
                            style={{ background: scope === s ? "var(--selected-bg)" : "transparent", color: scope === s ? "var(--text)" : "var(--text-muted)" }}>
                            {t(`tasks.scope.${s}`)}
                        </button>
                    ))}
                </div>
                <button data-testid="tasks-new-btn" onClick={() => setCreating(true)} className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium" style={{ background: "var(--primary)", color: "var(--primary-fg)" }}>
                    <Plus size={16} /> {t("tasks.new")}
                </button>
            </header>

            {err && <div className="rounded-md p-3 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}>{err}</div>}

            {creating && (
                <div className="rounded-xl border p-4 space-y-3" style={{ borderColor: "var(--border)", background: "var(--card)" }} data-testid="tasks-new-form">
                    <input data-testid="tasks-new-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={t("tasks.form.title") as string} className="w-full rounded-md px-3 py-2 text-sm outline-none border" style={{ background: "var(--input)", borderColor: "var(--border)", color: "var(--text)" }} />
                    <div className="flex items-center gap-2">
                        <select data-testid="tasks-new-priority" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="rounded-md px-2 py-1.5 text-sm border" style={{ background: "var(--input)", borderColor: "var(--border)", color: "var(--text)" }}>
                            {(["low", "normal", "high", "urgent"] as const).map((p) => <option key={p} value={p}>{t(`tasks.priority.${p}`)}</option>)}
                        </select>
                        <button data-testid="tasks-new-cancel" onClick={() => setCreating(false)} className="ml-auto text-sm" style={{ color: "var(--text-muted)" }}>{t("common.cancel")}</button>
                        <button data-testid="tasks-new-save" onClick={create} className="rounded-md px-3 py-1.5 text-sm font-medium" style={{ background: "var(--primary)", color: "var(--primary-fg)" }}>{t("common.save")}</button>
                    </div>
                </div>
            )}

            {loading && <div className="flex justify-center p-6"><Loader2 className="animate-spin" size={22} /></div>}
            {!loading && !rows.length && <div className="text-sm" style={{ color: "var(--text-muted)" }} data-testid="tasks-empty">{t("tasks.empty")}</div>}
            {!!rows.length && (
                <ul className="space-y-2">
                    {rows.map((row: any) => {
                        const p = PRIO_TONE[row.priority] || PRIO_TONE.normal;
                        const s = STATUS_TONE[row.status] || STATUS_TONE.new;
                        return (
                            <li key={row.id} className="rounded-lg border p-3 flex items-center gap-3" style={{ borderColor: "var(--border)", background: "var(--card)" }} data-testid={`tasks-row-${row.id}`}>
                                <button data-testid={`tasks-toggle-${row.id}`} onClick={() => toggle(row)} className="shrink-0 rounded-full w-6 h-6 border flex items-center justify-center" style={{ borderColor: "var(--border)", background: row.status === "done" ? "var(--primary)" : "transparent", color: row.status === "done" ? "var(--primary-fg)" : "transparent" }}>
                                    <Check size={14} />
                                </button>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium truncate" style={{ color: "var(--text)", textDecoration: row.status === "done" ? "line-through" : "none" }}>{row.title}</div>
                                    <div className="text-xs" style={{ color: "var(--text-muted)" }}>
                                        {row.due_at ? fmtDate(row.due_at) : t("tasks.no_due")}
                                    </div>
                                </div>
                                <span className="rounded-full px-2 py-0.5 text-xs" style={{ background: p.bg, color: p.fg }}>{t(`tasks.priority.${row.priority}`)}</span>
                                <span className="rounded-full px-2 py-0.5 text-xs" style={{ background: s.bg, color: s.fg }}>{t(`tasks.status.${row.status}`)}</span>
                                {confirmId === row.id ? (
                                    <span className="inline-flex items-center gap-1" data-testid={`tasks-delete-confirm-${row.id}`}>
                                        <button data-testid={`tasks-delete-yes-${row.id}`} onClick={() => remove(row.id)} disabled={deleting}
                                            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium disabled:opacity-60" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}>
                                            {deleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}{t("tasks.delete_confirm")}
                                        </button>
                                        <button data-testid={`tasks-delete-no-${row.id}`} onClick={() => setConfirmId(null)} aria-label={t("common.cancel")}
                                            className="inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-[var(--input)]" style={{ color: "var(--text-muted)" }}><X size={14} /></button>
                                    </span>
                                ) : (
                                    <button data-testid={`tasks-delete-${row.id}`} onClick={() => setConfirmId(row.id)} title={t("tasks.delete")} aria-label={t("tasks.delete")}
                                        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors hover:bg-[var(--error-bg)] hover:text-[var(--error-fg)]" style={{ color: "var(--text-muted)" }}>
                                        <Trash2 size={15} />
                                    </button>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
